import { useCallback, useEffect, useRef, useState } from "react";
import { checkValue, sourceMessages } from "@expertloop/i18n";
import { translationsApi, type TranslationFilter } from "./api";
import type { TranslationKeysResponse } from "./types";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export function useTranslationKeys(locale: string) {
	const [data, setData] = useState<TranslationKeysResponse | null>(null);
	const [filter, setFilter] = useState<TranslationFilter>("all");
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [page, setPage] = useState(1);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// La recherche porte sur 1300 clés côté serveur : inutile d'y aller à chaque frappe.
	useEffect(() => {
		const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [search]);

	const load = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await translationsApi.listKeys(locale, {
				filter,
				page,
				pageSize: PAGE_SIZE,
				...(debouncedSearch ? { q: debouncedSearch } : {}),
			});
			if (!isMounted.current) return;
			setData(response);
			setError("");
		} catch (err) {
			if (!isMounted.current) return;
			setError(err instanceof Error ? err.message : "load_failed");
		} finally {
			if (isMounted.current) setIsLoading(false);
		}
	}, [locale, filter, page, debouncedSearch]);

	useEffect(() => {
		void load();
	}, [load]);

	// Changer de filtre ou de recherche remet la pagination à zéro : rester
	// page 7 d'un résultat qui n'en compte que 2 afficherait une liste vide.
	const changeFilter = useCallback((next: TranslationFilter) => {
		setFilter(next);
		setPage(1);
	}, []);

	const changeSearch = useCallback((next: string) => {
		setSearch(next);
		setPage(1);
	}, []);

	return {
		data,
		filter,
		search,
		page,
		isLoading,
		error,
		changeFilter,
		changeSearch,
		setPage,
		reload: load,
	};
}

/**
 * Valide une correction manuelle avec la règle exacte du moteur, avant l'appel
 * réseau : l'utilisateur voit son erreur de placeholder immédiatement, et le
 * serveur reste l'autorité finale.
 */
export function validateOverride(key: string, value: string): string | null {
	const sourceValue = sourceMessages[key];
	if (sourceValue === undefined) return null;
	return checkValue(sourceValue, value, key);
}

/** Placeholders attendus pour une clé, à afficher en aide à la saisie. */
export function expectedPlaceholders(key: string): string[] {
	const sourceValue = sourceMessages[key] ?? "";
	return [...new Set(sourceValue.match(/\{\{\w+\}\}|\{\w+\}|%[sd]/g) ?? [])];
}
